import { Route, Router } from '@axa-fr-axion/router';
import { ActionsLayout, MainLayout, ScrollToTopOfLayout } from '@axa-fr-axion/layouts';
import { LoaderPage } from '@axa-fr-axion/loader';
import { Dossier } from '@axa-fr-axion/user-journey-services';
import '@axafr-btc-ds/colors/theme-vars.css';
import { NavigationPath, StepNavigationPath, TechNavigationPath } from 'interfaces';
import { lazy, useCallback, useEffect, useReducer, useState } from 'react';
import { journeyDataMocks } from 'mocks/data-mocks';
import { sendTagEventNavigation, shouldSendTagEventNavigation } from 'services/tag';
import classnames from 'classnames';

import {
    AppOptions,
    AppProps,
    ExtendedFunctionalError,
    JourneyData,
    JourneyDataValidityKeys,
    JourneyDataValue,
    JourneyInitialData,
    JourneyReducer,
} from './App.interface';
import { journeyReducer } from './App.reducer';
import {
    initJourneyDataWithOrigin,
    DEFAULT_OPTIONS,
    chooseEndStep,
    fromProductMapToProductCodes,
    folderReady,
    isCatalogVisibleFromOrigin,
    getInitialStep,
    forceMRPProductForMicroEntPreset,
    handlePreLegalStatusBody,
} from './App.utils';
import upperFirst from 'lodash.upperfirst';
import { ValidityStatus } from 'interfaces';
import { isAcceptedValidityStatus } from 'utils';
import lazyWithPreload from 'react-lazy-with-preload';
import { Stepper } from '@axa-fr-axion/stepper';
import { NextButton, PreviousButton, RenderIf } from 'components';
import { selectPreset } from 'presets';
import {
    getNextStepFrom,
    getPreviousStepFrom,
} from './App.router.utils';
// Step 0 - Init
import Initialization from 'features/initialization/Initialization';
import { getSelectionFromDefaultProductCodes } from 'features/catalog';
import { catalog } from 'services/catalog/Catalog.data';

// Mock mode only - Scenarios
const Scenarios = lazy(() => import('features/scenarios/Scenarios'));

// Step 1 - 0 - Recommandation
const RecommandationHome = lazyWithPreload(() => import('features/recommandation-home/RecommandationHome'));
// Step 1 - 1 - Catalog
const Catalog = lazyWithPreload(() => import('features/catalog/Catalog'));
// Step 2 - 1 - Company Information
export const Company = lazyWithPreload(() => import('features/company/Company'));
// Step 2 - 2 - Pre Economic Sector
export const PreEconomicSector = lazyWithPreload(() => import('features/pre-economic-sector/PreEconomicSector'));
// Step 2 - 3 - Economic Sector
export const EconomicSector = lazyWithPreload(() => import('features/economic-sector/EconomicSector'));
// Step 2 - 3.5 - Pre Legal Status
export const PreLegalStatus = lazyWithPreload(() => import('features/pre-legal-status/PreLegalStatus'));
// Step 2 - 4 - Legal Status
export const LegalStatus = lazyWithPreload(() => import('features/legal-status/LegalStatus'));
// Step 2 - 5 - Number of Employees
export const NumberOfEmployees = lazyWithPreload(() => import('features/number-of-employees/NumberOfEmployees'));
export const HasEmployees = lazyWithPreload(() => import('features/has-employees/HasEmployees'));
// Step 2 - 6 - Income
export const Income = lazyWithPreload(() => import('features/income/Income'));
// Step 2 - 7 - Years of Experience
export const YearsOfExperience = lazyWithPreload(() => import('features/years-of-experience/YearsOfExperience'));
// Step 2 - 8 - Catalog Recommandation
const PreCatalogRecommandation = lazyWithPreload(() => import('features/pre-catalog-recommandation/PreCatalogRecommandation'));
const CatalogRecommandation = lazyWithPreload(() => import('features/catalog-recommandation/CatalogRecommandation'));
// Step 2 - 9 - Has Peculiarities
export const HasPeculiarities = lazyWithPreload(() => import('features/has-peculiarities/HasPeculiarities'));
// Step 2 - 10 - Cumulative value of goods
export const CumulativeValueOfGoods = lazyWithPreload(() => import('features/cumulative-value-of-goods/CumulativeValueOfGoods'));
// Step 2 - 11 - Business premises presence
export const BusinessPremisesPresence = lazyWithPreload(() => import('features/business-premises-presence/BusinessPremisesPresence'));
// Step 2 - 12 - Business premises ownership
export const BusinessPremisesOwnership = lazyWithPreload(() => import('features/business-premises-ownership/BusinessPremisesOwnership'));
// Step 2 - 13 - Business premises Surface
export const BusinessPremisesSurface = lazyWithPreload(() => import('features/business-premises-surface/BusinessPremisesSurface'));
// Step 3 - Contact information
export const Identity = lazyWithPreload(() => import('features/contact/identity/Identity'));
export const HomeAddress = lazyWithPreload(() => import('features/contact/home-address/HomeAddress'));
// Optional step - Choose Advisor
export const PreChooseAdvisor = lazyWithPreload(() => import('features/pre-choose-advisor/PreChooseAdvisor'));
export const ChooseAdvisor = lazyWithPreload(() => import('features/choose-advisor/ChooseAdvisor'));

//OPtional Step - Pricing Needs
const PricingNeeds = lazy(() => import('features/pricing-needs/PricingNeeds'));

// Optional step - Tarification
const PreTarification = lazy(() => import('features/pre-tarification/PreTarification'));
const Tarification = lazy(() => import('features/tarification/Tarification'));
// Confirmation step
const PreConfirmation = lazy(() => import('features/pre-confirmation/PreConfirmation'));
// Final step
const End = lazy(() => import('features/end/End'));

// Technical error
const TechnicalError = lazyWithPreload(() => import('features/technical-error/TechnicalError'));

function App({ data, onScenarioChange, options, path }: AppProps): JSX.Element {
    const _options: AppOptions = {
        ...DEFAULT_OPTIONS,
        ...options,
    };

    const preset = selectPreset(_options.config?.preset);
    const initialStep = getInitialStep(_options, preset);

    const [ step, setStep ] = useState<NavigationPath>(path ?? initialStep);
    const [ isCatalogVisible, setIsCatalogVisible ] = useState<boolean>(isCatalogVisibleFromOrigin(options?.origine));
    const [ journeyData, dispatchJourneyData ] =
        useReducer<JourneyReducer, JourneyInitialData>(
            journeyReducer,
            data,
            // NOTE: Tant que GLM n'est pas en mesure d'ingérer le code MPE, nous somme contraint
            // de forcer la sélection du produit MRP pour le preset MicroEnt.
            forceMRPProductForMicroEntPreset(initJourneyDataWithOrigin(options?.origine), preset),
        );

    useEffect(() => {
        if (shouldSendTagEventNavigation(step, options?.config?.preset)) {
            sendTagEventNavigation(step, journeyData, options);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ step ]);

    const goToNextStep = (overrides?: Pick<Partial<JourneyData>, 'folder' | 'tarification'>) => {
        const newStep = getNextStepFrom(step, preset, {
            ...journeyData,
            ...overrides,
        });
        if (newStep) {
            setStep(newStep);
        }
    };

    const backToPreviousStep = () => {
        const newStep = getPreviousStepFrom(step, preset, journeyData);
        if (newStep) {
            setStep(newStep);
        }
    };

    const handleInitializationSuccess = (folder: Dossier): void => {
        dispatchJourneyData({ type: 'INITIALIZE', folder });

        // NOTE: In the case of pre selected product (Ex: marketing campaign), we have to "skip" the first step
        // and directly send the user to the next step, aka "Company".
        if (preset.screens?.catalog && !isCatalogVisible) {
            const [
                newSelection,
                newValidityStatus,
            ] = getSelectionFromDefaultProductCodes(journeyData.catalog.defaultProductCodes || [], catalog.groups);
            handleChange('catalog')?.({
                selectedProducts: newSelection,
            }, newValidityStatus);

            setIsCatalogVisible(true);
            setStep('Company');
        }
    };

    const handleTechnicalError = (): void => {
        setStep('Error');
    };

    const handleChange = useCallback((type: keyof Omit<JourneyData, 'folder'>) => {
        const validityKey = `is${ upperFirst(type) }Valid` as JourneyDataValidityKeys;
        return (newData: Omit<JourneyDataValue, 'folder'>, validityStatus: ValidityStatus): void => {
            dispatchJourneyData({
                type,
                payload: {
                    [ type ]: newData,
                    [ validityKey ]: isAcceptedValidityStatus(validityStatus),
                },
            });
        };
    }, [ dispatchJourneyData ]);

    const handleFunctionalError = (error?: ExtendedFunctionalError): void => {
        setStep(chooseEndStep(error?.status?.funnel));
        dispatchJourneyData({
            type: 'REQUEST_ERROR', error,
        });
        if (error?.conseiller) {
            dispatchJourneyData({
                type: 'SET_DEFAULT_ADVISOR',
                folder: {
                    ...journeyData?.folder,
                    lead: {
                        ...journeyData?.folder.lead,
                        conseillers: [ error?.conseiller ],
                    },
                },
            });
        }
    };

    const handleOnAbort = () => {
        setStep(chooseEndStep('ABORT'));
    };

    const LoaderComponent = <LoaderPage title="Veuillez patienter, nous chargeons la prochaine étape..." />;
    return (
        <MainLayout>
            <ScrollToTopOfLayout path={ step }>
                <Stepper<NavigationPath>
                    steps={ preset.stepper.steps }
                    data={ preset.stepper.data }
                    path={ step }
                />
            </ScrollToTopOfLayout>
            <section aria-label="Parcours Pro/Ent">
                <RenderIf value={ !journeyData.folder.id }>
                    <Initialization
                        data={ {
                            origine: _options.origine ?? {},
                            proprietes: _options.proprietes,
                        } }
                        onSuccess={ handleInitializationSuccess }
                        onTechnicalError={ handleTechnicalError }
                    />
                </RenderIf>
                <Router<NavigationPath>
                    current={ step }
                >
                    <Route<StepNavigationPath> path="RecommandationHome" fallback={ LoaderComponent }>
                        <RecommandationHome
                            className={ classnames({ 'hidden': !isCatalogVisible }) }
                            data={ undefined }
                            microEntParcoursLink={ _options.editorialSpecific?.microEntParcoursLink }
                            onChange={ handleChange('recommandation') }
                            onTechnicalError={ handleTechnicalError }
                        />
                        <RenderIf value={ isCatalogVisible }>
                            <ActionsLayout alignment="left">
                                <NextButton
                                    isDisabled={ !journeyData.isRecommandationValid }
                                    nextScreens={ journeyData.recommandation?.value === 'advice'
                                        ? [ Company, Catalog ]
                                        : undefined
                                    }
                                    onClick={ () => goToNextStep() }
                                />
                            </ActionsLayout>
                        </RenderIf>
                    </Route>
                    <Route<StepNavigationPath> path="Catalog" fallback={ LoaderComponent }>
                        <Catalog
                            className={ classnames({ 'hidden': !isCatalogVisible }) }
                            data={ {
                                ...journeyData.catalog,
                                showOphelie: !journeyData.recommandation?.value,
                            } }
                            onChange={ handleChange('catalog') }
                            onTechnicalError={ handleTechnicalError }
                        />
                        <RenderIf value={ isCatalogVisible } elseComponent={ LoaderComponent }>
                            <ActionsLayout alignment="right">
                                <RenderIf value={ !!journeyData.recommandation?.value }>
                                    <PreviousButton onClick={ () => backToPreviousStep() } />
                                </RenderIf>
                                <NextButton
                                    isDisabled={ !journeyData.isCatalogValid || !folderReady(journeyData) }
                                    nextScreens={ [ Company ] }
                                    onClick={ () => goToNextStep() }
                                />
                            </ActionsLayout>
                        </RenderIf>

                    </Route>
                    <Route<StepNavigationPath> path="Company" fallback={ LoaderComponent }>
                        <Company
                            data={ journeyData.company }
                            onChange={ handleChange('company') }
                            onSkip={ () => {
                                dispatchJourneyData({ type: 'RESET_COMPANY' });
                                setStep('EconomicSector');
                            } }
                            preset={ preset.type }
                        />
                        <ActionsLayout>
                            <RenderIf value={ initialStep !== 'Company' }>
                                <PreviousButton onClick={ () => backToPreviousStep() } />
                            </RenderIf>
                            <NextButton
                                isDisabled={ !journeyData.isCompanyValid }
                                nextScreens={ [ PreEconomicSector ] }
                                onClick={ () => {
                                    dispatchJourneyData({ type: 'SELECT_COMPANY' });
                                    goToNextStep();
                                } }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<TechNavigationPath> path="PreEconomicSector" fallback={ LoaderComponent }>
                        <PreEconomicSector
                            data={ {
                                productCodes: fromProductMapToProductCodes(journeyData.catalog.selectedProducts),
                                // NOTE: We can only reach this step when an establishment is selected
                                establishment: journeyData.company.establishment!,
                            } }
                            onSuccess={ () => goToNextStep() }
                            onFunctionalError={ handleFunctionalError }
                            onTechnicalError={ handleTechnicalError }
                        />
                        { LoaderComponent }
                    </Route>
                    <Route<StepNavigationPath> path="EconomicSector" fallback={ LoaderComponent }>
                        <RenderIf value={ folderReady(journeyData) } elseComponent={ LoaderComponent }>
                            <EconomicSector
                                data={ journeyData.economicSector }
                                options={ preset.screens.economicSector.options }
                                onChange={ (esData, esValidity) => dispatchJourneyData({
                                    type: 'SELECT_ECONOMIC_SECTOR',
                                    economicSector: esData,
                                    validity: esValidity,
                                }) }
                            />
                            <ActionsLayout>
                                <PreviousButton onClick={ () => backToPreviousStep() } />
                                <NextButton
                                    isDisabled={ !journeyData.isEconomicSectorValid }
                                    nextScreens={ [ PreLegalStatus ] }
                                    onClick={ () => goToNextStep() }
                                />
                            </ActionsLayout>
                        </RenderIf>
                    </Route>
                    <Route<TechNavigationPath> path="PreLegalStatus">
                        <PreLegalStatus
                            data={ {
                                productCodes: fromProductMapToProductCodes(journeyData.catalog.selectedProducts),
                                sector: journeyData.economicSector?.sector?.value,
                                establishment: handlePreLegalStatusBody(preset.type, journeyData),
                            } }
                            preset={ preset.type }
                            onSuccess={ () => goToNextStep() }
                            onFunctionalError={ handleFunctionalError }
                            onTechnicalError={ handleTechnicalError }
                        />
                        { LoaderComponent }
                    </Route>
                    <Route<StepNavigationPath> path="LegalStatus" fallback={ LoaderComponent }>
                        <LegalStatus
                            data={ journeyData.legalStatus }
                            options={ preset.screens.legalStatus.options }
                            onChange={ handleChange('legalStatus') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isLegalStatusValid }
                                nextScreens={ [ NumberOfEmployees ] }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="NumberOfEmployees" fallback={ LoaderComponent }>
                        <NumberOfEmployees
                            data={ journeyData.numberOfEmployees }
                            onChange={ handleChange('numberOfEmployees') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isNumberOfEmployeesValid }
                                nextScreens={ [ Income ] }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="Income" fallback={ LoaderComponent }>
                        <Income
                            data={ journeyData.income }
                            display={ preset.screens.income?.display }
                            onChange={ handleChange('income') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isIncomeValid }
                                nextScreens={ [ YearsOfExperience ] }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="YearsOfExperience" fallback={ LoaderComponent }>
                        <YearsOfExperience
                            data={ journeyData.yearsOfExperience }
                            onChange={ handleChange('yearsOfExperience') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isYearsOfExperienceValid }
                                nextScreens={ [ HomeAddress, Identity ] }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="HasEmployees" fallback={ LoaderComponent }>
                        <HasEmployees
                            data={ journeyData.hasEmployees }
                            onChange={ handleChange('hasEmployees') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isHasEmployeesValid }
                                nextScreens={ [ Income ] }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="HasPeculiarities" fallback={ LoaderComponent }>
                        <HasPeculiarities
                            data={ journeyData.hasPeculiarities }
                            onChange={ handleChange('hasPeculiarities') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isHasPeculiaritiesValid }
                                nextScreens={ [ CumulativeValueOfGoods ] }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="CumulativeValueOfGoods" fallback={ LoaderComponent }>
                        <CumulativeValueOfGoods
                            data={ journeyData.cumulativeValueOfGoods }
                            onChange={ handleChange('cumulativeValueOfGoods') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isCumulativeValueOfGoodsValid }
                                nextScreens={ [ BusinessPremisesPresence ] }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="BusinessPremisesPresence" fallback={ LoaderComponent }>
                        <BusinessPremisesPresence
                            data={ journeyData.businessPremisesPresence }
                            onChange={ handleChange('businessPremisesPresence') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isBusinessPremisesPresenceValid }
                                nextScreens={ [ BusinessPremisesOwnership ] }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="BusinessPremisesOwnership" fallback={ LoaderComponent }>
                        <BusinessPremisesOwnership
                            data={ journeyData.businessPremisesOwnership }
                            onChange={ handleChange('businessPremisesOwnership') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isBusinessPremisesOwnershipValid }
                                nextScreens={ [ BusinessPremisesSurface ] }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="BusinessPremisesSurface" fallback={ LoaderComponent }>

                        <BusinessPremisesSurface
                            data={ journeyData.businessPremisesSurface }
                            onChange={ handleChange('businessPremisesSurface') }
                        />

                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isBusinessPremisesSurfaceValid }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<TechNavigationPath> path="PreCatalogRecommandation" fallback={ LoaderComponent }>
                        <PreCatalogRecommandation
                            data={ {
                                codeSector: journeyData.economicSector.sector?.value ?? '5',
                                legalStatus: journeyData.legalStatus ?? '',
                                income: journeyData.income ?? '',
                                numberOfEmployees: journeyData.numberOfEmployees.slice?.label ?? '',
                            } }
                            onSuccess={ (products) => {
                                dispatchJourneyData({ type: 'SET_RECOMMANDATIONS', products });
                                goToNextStep();
                            } }
                            onTechnicalError={ handleTechnicalError }
                        />
                        { LoaderComponent }
                    </Route>
                    <Route<StepNavigationPath> path="CatalogRecommandation" fallback={ LoaderComponent }>
                        <CatalogRecommandation
                            data={ journeyData.catalogRecommandation }
                            onChange={ handleChange('catalogRecommandation') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isCatalogRecommandationValid }
                                nextScreens={ [ HomeAddress, Identity ] }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="Contact" fallback={ LoaderComponent }>
                        <HomeAddress
                            data={ journeyData.homeAddress }
                            onChange={ handleChange('homeAddress') }
                        />
                        <Identity
                            data={ journeyData.identity }
                            onChange={ handleChange('identity') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={
                                    !journeyData.isIdentityValid || !journeyData.isHomeAddressValid
                                }
                                nextScreens={ [ PreChooseAdvisor ] }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<TechNavigationPath> path="PreChooseAdvisor" fallback={ LoaderComponent }>
                        <PreChooseAdvisor
                            journeyData={ journeyData }
                            preset={ preset }
                            onSuccess={ (folder) => {
                                const newFolder: Dossier = {
                                    ...folder,
                                    lead: {
                                        ...folder.lead, hash: folder.lead?.hash ?? journeyData.folder?.lead?.hash,
                                    },
                                };

                                dispatchJourneyData({ type: 'SET_DEFAULT_ADVISOR', folder: newFolder });
                                goToNextStep({ folder });
                            } }
                            onFunctionalError={ handleFunctionalError }
                            onTechnicalError={ handleTechnicalError }
                        />
                        { LoaderComponent }
                    </Route>
                    <Route<NavigationPath> path="ChooseAdvisor" fallback={ LoaderComponent }>
                        <ChooseAdvisor
                            data={ journeyData.selectedAdvisor }
                            onChange={ (advisor) => {
                                dispatchJourneyData({ type: 'SELECT_ADVISOR', advisor });
                            } }
                            folder={ journeyData.folder }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.selectedAdvisor }
                                onClick={ () => goToNextStep() }
                            />
                        </ActionsLayout>
                    </Route>
                    <Route<TechNavigationPath> path="PricingNeeds" fallback={ LoaderComponent }>
                        <PricingNeeds
                            journeyData={ journeyData }
                            onSuccess={ (res) => {
                                const proprietes = [ ...(journeyData.folder.proprietes ?? []), ...res.proprietes ];
                                const folder = { ...journeyData.folder, proprietes };

                                dispatchJourneyData({ type: 'SET_CONFIRMATION', folder });
                                setStep('PreChooseAdvisor');
                            } }
                            onTechnicalError={ handleTechnicalError }
                            onFunctionalError={ handleFunctionalError }
                        />
                        { LoaderComponent }
                    </Route>
                    <Route<TechNavigationPath> path="PreTarification" fallback={ LoaderComponent }>
                        <PreTarification
                            journeyData={ journeyData }
                            onSuccess={ (res) => {
                                const { proprietes, tarifAnnuel, tarifMensuel, montantContribAttentat } = res;
                                handleChange('tarification')({ tarifAnnuel, tarifMensuel, montantContribAttentat }, 'Valid');
                                handleChange('proprietes')(proprietes, 'Valid');
                                goToNextStep();
                            } }
                            onTechnicalError={ handleTechnicalError }
                            onFunctionalError={ handleFunctionalError }
                        />
                        { LoaderComponent }
                    </Route>
                    <Route<TechNavigationPath> path="PreConfirmation" fallback={ LoaderComponent }>
                        <PreConfirmation
                            journeyData={ journeyData }
                            preset={ preset }
                            onSuccess={ (folder) => {
                                dispatchJourneyData({ type: 'SET_CONFIRMATION', folder });
                                setStep(chooseEndStep(folder.lead?.funnel));
                            } }
                            onTechnicalError={ handleTechnicalError }
                        />
                        { LoaderComponent }
                    </Route>
                    <Route<NavigationPath> path="End/*" fallback={ LoaderComponent }>
                        <End
                            current={ step }
                            options={ _options }
                            journeyData={ journeyData }
                        />
                    </Route>
                    <Route<NavigationPath> path="Tarification" fallback={ LoaderComponent }>
                        <Tarification
                            amount={ journeyData.tarification }
                            firstname={ journeyData.identity?.firstname }
                            dipaLink={ options?.editorialCommon?.dipaLink }
                            cgLink={ options?.editorialCommon?.cgLink }
                            handleFinaliserTarif={ () => goToNextStep() }
                            onAbort={ () => handleOnAbort() }
                        />
                    </Route>
                    <Route<StepNavigationPath> path="Error" fallback={ LoaderComponent }>
                        <TechnicalError />
                    </Route>
                    <Route<TechNavigationPath> path="Scenarios" fallback={ LoaderComponent }>
                        <Scenarios
                            data={ journeyDataMocks }
                            onChange={ onScenarioChange }
                            onReady={ (dataMock?: JourneyData) => {
                                if (dataMock) {
                                    dispatchJourneyData({
                                        type: 'folder',
                                        payload: dataMock,
                                    });
                                }
                                setStep(initialStep);
                            } }
                        />
                    </Route>
                </Router>
            </section>
        </MainLayout>
    );
}

export default App;
